import React, { useEffect, useState } from "react";
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth, ensureUserProfile, getUserProfile } from "../firebase";

function LoginPage({ user, role, loading }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    if (!loading && user && role === "admin") {
      navigate("/admin", { replace: true });
    }
  }, [loading, user, role, navigate]);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!auth) {
      setStatus("Firebase is not configured yet. Add your Firebase environment variables.");
      return;
    }

    setSubmitting(true);
    setStatus("");

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const profile = await ensureUserProfile(result.user);

      if (profile.role !== "admin") {
        await signOut(auth);
        setStatus("This account does not have admin access.");
        return;
      }

      const freshProfile = await getUserProfile(result.user.uid);
      if (freshProfile?.role === "admin") {
        navigate("/admin", { replace: true });
      }
    } catch (error) {
      setStatus(error.message || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!auth) {
      setStatus("Firebase is not configured yet. Add your Firebase environment variables.");
      return;
    }

    if (!email.trim()) {
      setStatus("Bitte geben Sie zuerst Ihre E-Mail-Adresse ein.");
      return;
    }

    setResettingPassword(true);
    setStatus("");

    try {
      await sendPasswordResetEmail(auth, email);
      setStatus("Passwort-Zurücksetzungs-E-Mail gesendet. Bitte überprüfen Sie Ihren Posteingang. (Falls Sie die E-Mail nicht in Ihrem Posteingang finden, überprüfen Sie bitte auch Ihren Spam-Ordner.)");
    } catch (error) {
      setStatus(error.message || "Passwort konnte nicht zurückgesetzt werden.");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleLogout = async () => {
    if (!auth) {
      return;
    }

    await signOut(auth);
    navigate("/login", { replace: true });
  };

  if (loading) {
    return <div className="auth-page"><p>Loading...</p></div>;
  }

  if (user && role === "admin") {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Admin logged in</h2>
          <p>You are already signed in as an administrator.</p>
          <button type="button" onClick={() => navigate("/admin")}>Open admin panel</button>
          <button type="button" className="secondary-btn" onClick={handleLogout}>Log out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h2>Admin login</h2>
        <form onSubmit={handleLogin} className="auth-form">
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>

          <button type="button" className="reset-password-link" onClick={handlePasswordReset} disabled={resettingPassword}>
            {resettingPassword ? "Sending reset email..." : "Passwort vergessen"}
          </button>

          {status && <p className="auth-status">{status}</p>}

          <button type="submit" disabled={submitting}>
            {submitting ? "Signing in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
